import { AdvancedTable } from '@/components/ui/AdvancedTable';
import { Button } from '@/components/ui/Button';
import { CardBox } from '@/components/ui/CardBox';
import { Icon } from '@/components/ui/Icon';
import { AppVersion } from '@/pages/app-versions/types';
import { mdiAndroid, mdiApple, mdiDelete, mdiEye, mdiPencil } from '@mdi/js';
import * as React from 'react';

export interface AppVersionsTableProps {
  appVersions: AppVersion[];
  isLoading: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (key: string) => void;
  onRowClick: (appVersion: AppVersion) => void;
  onEditAppVersion: (appVersion: AppVersion) => void;
  onDeleteAppVersion: (appVersion: AppVersion) => void;
  isDeleting: boolean;
}

export const AppVersionsTable: React.FC<AppVersionsTableProps> = ({
  appVersions,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  onEditAppVersion,
  onDeleteAppVersion,
  isDeleting,
}) => {
  const columns = [
    { key: 'platform', label: 'Platform', sortable: true, className: 'w-32' },
    { key: 'versions', label: 'Versions', sortable: false },
    {
      key: 'forceUpdate',
      label: 'Force Update',
      sortable: false,
      className: 'w-32',
    },
    { key: 'status', label: 'Status', sortable: false, className: 'w-24' },
    { key: 'createdAt', label: 'Created', sortable: true, className: 'w-32' },
  ];

  const renderCell = (
    key: string,
    value: string | number | boolean | string[] | undefined,
    item: AppVersion,
  ): React.ReactNode => {
    switch (key) {
      case 'platform':
        return (
          <div className='flex items-center space-x-2'>
            <Icon
              path={item.platform === 'ios' ? mdiApple : mdiAndroid}
              size={24}
              className={
                item.platform === 'ios' ? 'text-grey-800' : 'text-green-600'
              }
            />
            <span className='font-medium text-grey-900 capitalize'>
              {item.platform === 'ios' ? 'iOS' : 'Android'}
            </span>
          </div>
        );
      case 'versions':
        return (
          <div className='space-y-1'>
            <div className='flex items-center space-x-2'>
              <span className='text-xs text-grey-500 w-16'>Minimum:</span>
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
                {item.minimumVersion}
              </span>
            </div>
            <div className='flex items-center space-x-2'>
              <span className='text-xs text-grey-500 w-16'>Latest:</span>
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
                {item.latestVersion}
              </span>
            </div>
          </div>
        );
      case 'forceUpdate':
        return item.forceUpdate ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700'>
            Forced
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-grey-100 text-grey-600'>
            Optional
          </span>
        );
      case 'status':
        return item.enabled ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
            Enabled
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
            Disabled
          </span>
        );
      case 'createdAt':
        return (
          <div className='text-sm text-grey-900'>
            {item.createdAt
              ? new Date(item.createdAt).toLocaleDateString()
              : 'N/A'}
          </div>
        );
      default:
        return String(value || '-');
    }
  };

  const renderActions = (appVersion: AppVersion) => (
    <div className='flex items-center space-x-2'>
      <Button
        icon={mdiEye}
        small
        color='secondary'
        title='View Details'
        onClick={() => onRowClick(appVersion)}
      />
      <Button
        icon={mdiPencil}
        small
        color='secondary'
        title='Edit Version'
        onClick={() => onEditAppVersion(appVersion)}
      />
      <Button
        icon={mdiDelete}
        small
        color='danger'
        title='Delete Version'
        loading={isDeleting}
        onClick={() => onDeleteAppVersion(appVersion)}
      />
    </div>
  );

  return (
    <CardBox hasTable>
      <AdvancedTable
        columns={columns}
        data={appVersions}
        loading={isLoading}
        onSort={onSort}
        sortKey={sortBy}
        sortDirection={sortOrder}
        renderCell={renderCell}
        renderActions={renderActions}
        onRowClick={onRowClick}
        emptyMessage='No app versions found. Add a version configuration to control mobile app updates.'
      />
    </CardBox>
  );
};
